import type { ApiClient } from '@ghp/api-client'
import type { SessionUser } from '@ghp/domain'
import {
  AppShell,
  AppShellContent,
  Button,
  NavItem,
  ReportProblem,
  type ReportProblemProps,
} from '@ghp/ui'
import { useQuery, type QueryClient } from '@tanstack/react-query'
import {
  Link,
  Outlet,
  createRootRouteWithContext,
  createRoute,
  redirect,
  useNavigate,
  useRouterState,
  type LinkProps,
} from '@tanstack/react-router'
import {
  Bell,
  CalendarDays,
  ClipboardList,
  History,
  LogOut,
  MessageSquareWarning,
  Settings,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useId, useState, useSyncExternalStore } from 'react'
import type { AuthStore } from '../../features/auth/auth-store'
import { ConnectionPanel } from '../../features/auth/components/ConnectionPanel'
import type { GeolocationPort } from '../../features/inspections/ports/geolocation'
import { attentionOf } from '../../features/sync/attention'
import type { PendingUpdate } from '../../offline/pending-update'
import {
  FIELD_APPOINTMENTS_QUERY_KEY,
  INQUIRIES_QUERY_KEY,
  OUTBOX_QUERY_KEY,
} from '../../offline/query-keys'
import type { SharedDatabase } from '../../offline/shared-db'
import type { TeamDataStore } from '../../offline/team-data'
import {
  CONNECTION_ICON,
  CONNECTION_LABEL,
  useConnectionStatus,
  type ConnectionStatus,
} from '../connection'
import { captureScreen, isErrorReportingEnabled, sendReport } from '../error-reporting'
import { useTeamData } from '../use-team-data'
import s from './root.module.css'

/**
 * Ce que la tablette apporte aux écrans, en plus du routeur.
 *
 * Injecté plutôt qu'importé : un écran doit pouvoir être monté sur une **base vide et un
 * réseau muet** — l'état d'un sous-sol autant que celui d'un test. Un module importé en dur
 * rendrait ce montage impossible sans mocker la couche de stockage.
 */
export interface FieldContext {
  /**
   * Les bases locales — **source de vérité des lectures** (D21) : les `queryFn` lisent Dexie.
   *
   * ⚠️ Le **porteur**, pas une base : il y en a une par équipe et une seule est ouverte à la
   * fois (D22 pt.4, `offline/team-data.ts`). Un écran ne le lit pas directement — il prend
   * `useTeamData()`, qui rend la base du moment et redessine l'écran quand la tablette change
   * de main.
   */
  data: TeamDataStore
  /**
   * Ce qui n'appartient à aucune équipe : le référentiel d'anomalies, le journal de diagnostic
   * et les réglages de l'appareil (`offline/shared-db.ts`). Une base, celle-là, parce qu'elle
   * survit aux changements de main.
   */
  shared: SharedDatabase
  /** Les quatre appels de synchro (D4) : deux qui montent, deux qui descendent. */
  sync: ApiClient['sync']
  /**
   * La correction des coordonnées d'une demande, **en ligne** — la seconde exception à D4 : une
   * écriture directe, jamais par l'outbox (carte « Coordonnées » de l'éditeur).
   */
  contacts: Pick<ApiClient['inquiries'], 'updateContacts'>
  /**
   * Le **déplacement** d'un rendez-vous, en ligne — même exception à D4 que les coordonnées : il
   * annule un créneau que l'usager a reçu, un rejeu tardif écraserait celui du bureau.
   */
  planning: Pick<ApiClient['planning'], 'recordFieldAppointment'>
  /**
   * Le jeton d'accès vu par un écran, **s'il y en a un** — `null` en session dégradée, ce qui
   * est un état de fonctionnement normal (D22 pt.1) et non une panne.
   *
   * Une fonction, pas une valeur : la session monte en ligne toute seule au retour du réseau, et
   * un écran monté avant ne doit pas rester avec un `null` figé.
   */
  accessToken: () => string | null
  /**
   * Le GPS de la tablette, derrière un port (D13 — PWA, pas Capacitor). jsdom n'en a pas, et
   * une permission refusée est un cas de fonctionnement, pas une panne.
   */
  geolocation: GeolocationPort
  /**
   * Une passe de synchro complète — refresh, envoi de la file, descentes — à la demande d'un
   * écran. **Ne rejette jamais.** C'est elle qu'un bouton appelle, jamais `flushOutbox` nu :
   * un envoi relancé sans refresh repartirait avec le jeton qui vient d'être refusé (D22).
   *
   * Le journal de synchro l'appelle derrière « Synchroniser » : la file repart d'elle-même au
   * retour du réseau, ce bouton ne sert qu'à ne pas attendre le tour de minuteur suivant.
   */
  requestSync: () => Promise<void>
  /**
   * La nouvelle version précachée qui **attend** l'accord de l'agent (`registerType: 'prompt'`).
   *
   * Injecté comme le reste, et pour une raison de plus qu'ailleurs : le module qui l'alimente
   * importe `virtual:pwa-register`, que seul le build de production résout — un écran qui
   * l'importerait en dur ne serait plus montable du tout en test.
   */
  pendingUpdate: PendingUpdate
}

/**
 * Contexte injecté dans toutes les routes — même forme que le back-office : le
 * `QueryClient` y est disponible pour les futurs `loader` (intégration D19/D21), et la
 * session (`auth`) pour les gardes de route.
 */
export interface RouterContext extends FieldContext {
  queryClient: QueryClient
  auth: AuthStore
}

/** La racine ne rend qu'un `Outlet` : l'écran de connexion vit hors de la coquille. */
/**
 * Hors de la coquille — la connexion —, le bouton « Signaler un problème » (#481) flotte. Dans la
 * coquille, il est une entrée du rail : il y est toujours au même endroit, et ne recouvre rien.
 */
export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
})

function Root() {
  const inShell = useRouterState({
    select: (state) => state.matches.some((match) => match.routeId === shellRoute.id),
  })

  return (
    <>
      <Outlet />
      {!inShell && <ReportButton />}
    </>
  )
}

function ReportButton({ trigger }: Pick<ReportProblemProps, 'trigger'>) {
  const { auth } = rootRoute.useRouteContext()
  if (!isErrorReportingEnabled()) return null

  return (
    <ReportProblem
      trigger={trigger}
      capture={captureScreen}
      onSend={(report) => {
        const session = auth.getSnapshot()
        return sendReport(report, session.state === 'signed_out' ? undefined : session.user.id)
      }}
    />
  )
}

export const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: Shell,
  beforeLoad: async ({ context }) => {
    await context.auth.ready
    if (context.auth.getSnapshot().state === 'signed_out') {
      throw redirect({ to: '/login' })
    }
  },
})

/**
 * **L'état de connexion, en pied de rail — et le seul geste qui le répare.**
 *
 * Ce n'était qu'un témoin, et il mentait : il lisait `navigator.onLine`, donc il affichait
 * « En ligne » pendant que la déconnexion juste en dessous se fermait sur une session que le
 * serveur n'avait pas confirmée. Deux vérités partielles côte à côte, dont la somme se lit comme
 * un bug (#141). Les deux lisent désormais la **même** valeur (`app/connection.ts`).
 *
 * Et c'est un **bouton**, parce qu'un agent qui voit un état anormal doit pouvoir agir dessus :
 * le panneau porte l'explication, un essai immédiat, et la ressaisie des identifiants quand le
 * serveur a refusé la session. Sans lui, la seule issue connue de l'agent était le rechargement
 * de page — qui ne répare rien, et qui perd le contexte de sa saisie.
 */
function ConnectionState() {
  const { auth, requestSync } = shellRoute.useRouteContext()
  const session = useSyncExternalStore(auth.subscribe, auth.getSnapshot, auth.getSnapshot)
  const status = useConnectionStatus(auth)
  const [panelOpen, setPanelOpen] = useState(false)
  const Icon = CONNECTION_ICON[status]

  return (
    <>
      <button
        type="button"
        className={s.network}
        data-status={status}
        // Ce que le bouton ouvre — sans quoi rien n'annonce qu'il y a quelque chose derrière.
        aria-haspopup="dialog"
        /*
         * ⚠️ Un nom **fixe**, qui ne suit pas l'état affiché. Le contrôle est le même quoi qu'il
         * annonce : laisser son nom accessible varier — « En ligne », puis « Session expirée » —
         * ferait disparaître et réapparaître un bouton différent pour qui navigue au lecteur
         * d'écran, et rendrait toute automatisation dépendante du libellé du moment.
         */
        aria-label="État de la connexion"
        onClick={() => setPanelOpen(true)}
      >
        <Icon size={23} aria-hidden />
        {/* `role="status"` : l'état change tout seul — au retour du réseau, ou quand une passe
            de synchro aboutit — et il doit s'annoncer sans que l'agent y revienne. */}
        <span role="status">{CONNECTION_LABEL[status]}</span>
      </button>

      <ConnectionPanel
        open={panelOpen}
        onOpenChange={setPanelOpen}
        status={status}
        session={session}
        // Enveloppé plutôt que passé nu : `signIn` est une méthode du store, et la détacher de
        // son objet est exactement ce que `no-unbound-method` interdit de faire sans y penser.
        signIn={(credentials) => auth.signIn(credentials)}
        onRetry={requestSync}
      />
    </>
  )
}

/**
 * Les initiales à poser sur l'avatar.
 *
 * Un compte d'équipe est créé avec deux initiales, un compte nominatif avec un nom et un
 * prénom (M22) : les deux arrivent ici dans les mêmes champs. Le repli sur le `login` n'est
 * pas décoratif — un avatar vide ne dirait pas *quel* compte est ouvert, et c'est la seule
 * chose que deux agents partageant une tablette ont besoin de vérifier d'un coup d'œil.
 */
export function initialsOf(user: SessionUser): string {
  const fromName = [user.firstName, user.lastName]
    .map((part) => part?.trim().charAt(0) ?? '')
    .join('')
    .toUpperCase()

  return fromName === '' ? user.login.slice(0, 2).toUpperCase() : fromName
}

/** Le nom à annoncer, à défaut le `login` : on n'annonce jamais un avatar muet. */
export function nameOf(user: SessionUser): string {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  return full === '' ? user.login : full
}

/**
 * L'agent connecté, en pied de rail (prototype `.rail-avatar`).
 *
 * `role="img"` avec son nom accessible : les initiales seules ne se lisent pas — un lecteur
 * d'écran épellerait « JL ». Non interactif, contrairement au prototype où l'avatar ouvre les
 * réglages : le rail porte désormais sa propre entrée « Réglages », et doubler ce chemin par une
 * seconde cible non libellée n'ajouterait qu'une ambiguïté au doigt comme au lecteur d'écran.
 */
function SignedInAgent({ user }: { user: SessionUser }) {
  return (
    <span className={s.avatar} role="img" aria-label={`Agent connecté : ${nameOf(user)}`}>
      {initialsOf(user)}
    </span>
  )
}

/**
 * Le délai de garde de la déconnexion.
 *
 * `api.logout` ne pose ni délai ni `AbortSignal` : une API qui accepte la connexion mais ne
 * répond jamais laisserait le geste en suspens, les deux boutons fermés et sans autre issue
 * qu'un rechargement. Dix secondes : au-delà, l'agent reprend la main plutôt que d'attendre
 * une réponse qui ne viendra pas.
 *
 * ⚠️ La requête n'est pas annulée pour autant — le client HTTP n'expose pas d'`AbortSignal` : si
 * le serveur finit par répondre, la session locale s'efface et le pied du rail se vide de
 * lui-même. C'est le seul dénouement qui ne coûte rien à l'agent, la tournée vivant ailleurs.
 */
export const SIGN_OUT_TIMEOUT_MS = 10_000

/**
 * Ce que l'agent lit sous « Déconnexion » — **deux choses distinctes à la même place**, et jamais
 * un contrôle grisé et muet, qui se lit comme un bug (M21).
 *
 * Les trois premières phrases disent pourquoi le départ est **fermé**, et elles demandent toutes
 * d'attendre quelque chose. La quatrième ne ferme rien : le départ est ouvert, elle dit ce qu'il
 * **coûte**. C'est exactement la distinction que la session ne portait pas avant #141, et le
 * motif pour lequel l'écran disait « Session non confirmée » à tout le monde.
 */
const LEAVE_NOTICE: Record<Exclude<ConnectionStatus, 'online'>, string> = {
  offline: 'Hors réseau : la reconnexion exige l’API',
  checking: 'Session non confirmée : la reconnexion exige l’API',
  unreachable: 'Serveur injoignable : la reconnexion exige l’API',
  sign_in_required: 'Session refusée : partir est possible, rentrer exigera l’API',
}

type LeaveOutcome = 'done' | 'failed' | 'timed_out'

/** Ce que l'agent lit quand le départ n'aboutit pas — jamais deux boutons figés et muets (M21). */
const LEAVE_FAILURE: Record<Exclude<LeaveOutcome, 'done'>, string> = {
  failed: 'Déconnexion impossible : la session n’a pas pu être effacée.',
  timed_out: 'Serveur sans réponse : la déconnexion n’a pas abouti.',
}

/** La déconnexion, bornée dans le temps : ni un rejet ni une absence de réponse ne bloque. */
async function attemptSignOut(signOut: () => Promise<void>): Promise<LeaveOutcome> {
  let expiry: ReturnType<typeof setTimeout> | undefined
  const guard = new Promise<LeaveOutcome>((resolve) => {
    expiry = setTimeout(() => resolve('timed_out'), SIGN_OUT_TIMEOUT_MS)
  })

  try {
    return await Promise.race<LeaveOutcome>([
      signOut().then(
        () => 'done',
        () => 'failed',
      ),
      guard,
    ])
  } finally {
    clearTimeout(expiry)
  }
}

/**
 * Le pied du rail : à qui est la tablette, et comment la rendre.
 *
 * L'équipe est lue dans la session **locale** (D22 pt.1) : elle s'affiche en dégradé comme en
 * ligne, sans rien demander au serveur. Un agent sans équipe entre quand même (M6) — l'écran
 * le dit au lieu de laisser un vide.
 *
 * ⚠️ **La déconnexion est fermée tant qu'on ignore si le serveur répondra** (#108, décision du
 * 02/09). Se reconnecter exige l'API : un agent qui se déconnecte dans un vide sanitaire se
 * verrouille dehors, et perd l'accès aux contrôles en cours sur la tablette. On ne peut pas se
 * verrouiller dehors si la porte refuse de se fermer. Trois états ferment donc le départ, et pas
 * le seul `navigator.onLine` à `false` — une interface réseau active ne dit pas que le serveur
 * répond, et c'est le serveur qui rouvrira la porte. Les saisies, elles, ne partent jamais avec
 * une déconnexion — elles vivent dans une autre base (D22) et **y restent** : le cloisonnement
 * par équipe les rend invisibles à l'agent suivant, il ne les supprime pas (D22 pt.4).
 *
 * ⚠️ La raison affichée **n'attribue aucune panne qui n'ait été constatée**. Au démarrage à
 * froid la session est `degraded` avant qu'aucun refresh n'ait été tenté (`enterFromLocal`) :
 * dire « serveur injoignable » à cet instant serait inventer une cause, ce que M21 interdit.
 * C'est `SessionDoubt` qui lève l'ambiguïté depuis #141 — l'écran ne nomme le serveur que
 * lorsqu'un refresh est réellement parti sans revenir, et il renvoie au panneau de connexion
 * quand la seule issue est de ressaisir des identifiants.
 *
 * ⚠️ La confirmation en deux temps reste, même en ligne : le bouton est à portée du pouce qui
 * tient la tablette, et le geste est irréversible sans réseau.
 */
function TeamBlock() {
  const { auth, queryClient } = shellRoute.useRouteContext()
  const navigate = useNavigate()
  const session = useSyncExternalStore(auth.subscribe, auth.getSnapshot)
  const status = useConnectionStatus(auth)
  const [confirming, setConfirming] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const noticeId = useId()
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  // ⚠️ La coquille survit à la navigation : sans ce désarmement, une question ouverte par
  // erreur reste posée d'un écran à l'autre, et le pouce qui retombe plus tard là où
  // « Déconnexion » se trouvait touche « Quitter » — le geste que la confirmation évite.
  useEffect(() => {
    setConfirming(false)
    setFailure(null)
  }, [pathname])

  // La coquille ne se rend jamais déconnectée (garde de `shellRoute`) : rien à annoncer.
  if (session.state === 'signed_out') return null

  /**
   * ⚠️ **Le départ se ferme sur le silence du serveur, pas sur son refus** (#222). La règle de
   * #108 n'a pas bougé — il faut l'API pour rentrer, donc on ne sort pas tant qu'on ignore si
   * elle répondra — et elle se lit sur le même état que le rail, une seule valeur pour les deux.
   *
   * ⚠️ **`sign_in_required` rouvre le départ, et c'est tout le correctif.** Le serveur a répondu
   * et il refuse cette session : attendre ne la rétablira pas. Tenir la porte fermée dans ce
   * cas-là piégeait la tablette au moment précis où le bureau verrouillait le compte de l'équipe
   * — ni travail, ni déconnexion, ni reconnexion — et déverrouiller ne la libérait pas, les
   * sessions étant révoquées avec le compte. Le risque que cette fermeture couvrait — un `401`
   * qui viendrait d'un portail captif de chantier — reste vrai et ne justifie pas un cul-de-sac :
   * il devient l'**avertissement** posé sous le bouton (`LEAVE_NOTICE`), lu avant une
   * confirmation qui demande déjà deux gestes (M21, avertir plutôt que bloquer). La ressaisie sur
   * place reste offerte dans le panneau de connexion, et reste le meilleur chemin quand elle peut
   * aboutir : elle ne touche ni la session locale ni les saisies en cours.
   */
  const canLeave = status === 'online' || status === 'sign_in_required'

  async function leave(): Promise<void> {
    setLeaving(true)
    setFailure(null)

    const outcome = await attemptSignOut(() => auth.signOut())
    if (outcome !== 'done') {
      // La session locale est intacte : l'agent est toujours sur sa tournée, et peut réessayer.
      setLeaving(false)
      setFailure(LEAVE_FAILURE[outcome])
      return
    }

    /**
     * ⚠️ **Le cache de lecture, lui, part** — et il est le seul à partir : les saisies vivent
     * dans une autre base, que ce geste ne touche pas (D22). Le départ **navigue** sans recharger
     * la page, donc rien ne vide de lui-même un cache qui ne périme jamais
     * (`staleTime: Infinity`).
     *
     * C'est une **ceinture**, pas le dispositif : le cloisonnement tient au périmètre d'équipe
     * dans les clés (`offline/query-keys.ts`), qui couvre aussi le changement d'équipe **sans**
     * déconnexion — un agent de deux équipes qui rouvre sa session ne repasse pas ici. Mais elle
     * reste vraie si quelqu'un oublie de scoper une clé plus tard, et elle rend à la tablette la
     * mémoire des photos lues, ce que le périmètre seul ne fait pas.
     */
    queryClient.clear()

    await navigate({ to: '/login' })
  }

  return (
    <div className={s.team}>
      <span className={s.teamName} title={session.team?.name ?? undefined}>
        {session.team?.name ?? 'Sans équipe'}
      </span>
      <span className={s.teamLogin}>{session.user.login}</span>

      {confirming ? (
        <div className={s.leave}>
          {/* La question, sans quoi « Quitter » se lirait « quitter l'application ». */}
          <span className={s.confirm}>Se déconnecter ?</span>
          <Button
            tone="danger"
            size="sm"
            // ⚠️ La coupure peut survenir **pendant** que la question est posée : le départ se
            // ferme alors sans refermer la question, sinon l'agent verrait son geste disparaître.
            aria-disabled={!canLeave}
            aria-describedby={status === 'online' ? undefined : noticeId}
            disabled={leaving}
            onClick={() => {
              if (canLeave) void leave()
            }}
          >
            Quitter
          </Button>
          <Button
            tone="ghost"
            size="sm"
            className={s.cancel}
            disabled={leaving}
            onClick={() => setConfirming(false)}
          >
            Annuler
          </Button>
        </div>
      ) : (
        <Button
          tone="ghost"
          size="sm"
          className={s.signOut}
          // ⚠️ `aria-disabled` et non l'attribut natif : un bouton `disabled` sort de l'ordre de
          // tabulation, et la raison portée par `aria-describedby` ne serait jamais annoncée —
          // le contrôle fermé resterait muet pour qui navigue au clavier. Le clic est donc
          // neutralisé à la main, et la feuille de style peint la fermeture.
          aria-disabled={!canLeave}
          aria-describedby={status === 'online' ? undefined : noticeId}
          onClick={() => {
            if (canLeave) setConfirming(true)
          }}
        >
          <LogOut size={20} aria-hidden />
          Déconnexion
        </Button>
      )}

      {/* M21 — avertir plutôt que bloquer sans rien dire : un bouton fermé sans explication se
          lit comme un bug. `role="status"` pour que la raison soit annoncée à la fermeture, et
          `aria-describedby` pour qu'elle le soit aussi à la prise de focus sur le bouton. */}
      {/* `status` plutôt que `!canLeave` : les deux ne disent plus la même chose — un départ
          ouvert porte encore un avertissement — et seul le premier apprend au compilateur
          qu'« en ligne » est exclu, donc qu'aucune phrase n'a à exister pour ce cas. */}
      {status !== 'online' && (
        <span className={s.notice} id={noticeId} role="status">
          {LEAVE_NOTICE[status]}
        </span>
      )}

      {failure !== null && (
        <span className={s.notice} role="alert">
          {failure}
        </span>
      )}
    </div>
  )
}

interface RailItemProps {
  to: LinkProps['to']
  icon: LucideIcon
  label: string
  /** Pour la route `/` seulement : sans lui, le lien serait actif sur tous les écrans. */
  exact?: boolean
  badge?: { count: number; label: string }
}

/**
 * Un lien du rail — le `NavItem` de `@ghp/ui` en orientation verticale (icône au-dessus du
 * libellé), habillant le `Link` de TanStack Router. L'état actif n'est pas un état React :
 * le routeur pose `aria-current="page"` sur le lien actif, et le style s'appuie dessus.
 *
 * Ce qui reste ici : le jeu d'icônes, leur taille, et le routage. L'apparence du lien est
 * partagée avec le bandeau du back-office.
 */
function RailItem({ to, icon: Icon, label, exact, badge }: RailItemProps) {
  return (
    <NavItem
      orientation="vertical"
      icon={<Icon size={23} aria-hidden />}
      label={label}
      badge={badge}
      render={<Link to={to} activeOptions={{ exact }} />}
    />
  )
}

/**
 * « Notifs », avec la pastille du prototype (`.rail-badge`) — mais elle compte **ce qui attend
 * l'agent** (`attentionOf`), pas des non-lus : l'app n'a pas de flux d'activité, et compter les
 * envois dirait « 3 non lues » à propos de trois gestes que l'agent a lui-même faits.
 *
 * Mêmes clés de requête que l'écran Notifications : un seul cache, que l'observateur Dexie
 * invalide à chaque écriture — la pastille bouge dès qu'un renvoi descend ou qu'un refus tombe.
 */
function NotificationsRailItem() {
  const data = useTeamData()
  const outbox = useQuery({
    queryKey: OUTBOX_QUERY_KEY,
    queryFn: () => data.outbox.toArray(),
  })
  const inquiries = useQuery({
    queryKey: INQUIRIES_QUERY_KEY,
    queryFn: () => data.inquiries.toArray(),
  })
  const appointments = useQuery({
    queryKey: FIELD_APPOINTMENTS_QUERY_KEY,
    queryFn: () => data.appointments.toArray(),
  })
  const { count } = attentionOf(outbox.data ?? [], inquiries.data ?? [], appointments.data ?? [])

  return (
    <RailItem
      to="/notifications"
      icon={Bell}
      label="Notifs"
      badge={{ count, label: `${String(count)} à traiter` }}
    />
  )
}

/**
 * **L'invite de mise à jour** — le seul geste qui fait entrer un déploiement dans un onglet
 * déjà ouvert (#224).
 *
 * `registerType: 'prompt'` précache la nouvelle version et l'y laisse attendre, plutôt que de
 * recharger l'app sous les doigts d'une équipe en pleine saisie. C'est le bon choix, et il
 * coûtait jusqu'ici exactement ce qu'il promettait d'éviter : personne ne pouvant donner
 * l'accord, la version restait en `waiting` indéfiniment et le correctif n'atteignait jamais
 * la tablette — rechargement compris, le Service Worker contrôlant la page.
 */
function UpdatePrompt() {
  const { pendingUpdate } = shellRoute.useRouteContext()
  const apply = useSyncExternalStore(pendingUpdate.subscribe, pendingUpdate.read, () => null)
  const [setAside, setSetAside] = useState(false)
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  // ⚠️ **Une invite écartée ne se perd jamais.** Sans ce réarmement, un « Plus tard » vaudrait
  // « plus jamais » : la version attendrait indéfiniment et le déploiement n'entrerait pas —
  // le défaut de #224, reconstitué par l'invite censée le corriger. Le changement d'écran est
  // le moment où l'agent est entre deux tâches, donc le moment où on peut le lui redemander.
  useEffect(() => setSetAside(false), [pathname])

  if (apply === null || setAside) return null

  return (
    <p className={s.update} role="status">
      Nouvelle version disponible
      <Button size="sm" onClick={apply}>
        Mettre à jour
      </Button>
      <Button tone="ghost" size="sm" onClick={() => setSetAside(true)}>
        Plus tard
      </Button>
    </p>
  )
}

/**
 * Coquille de l'application (`Shell`) — l'*app shell* précaché par le Service Worker,
 * d'après le rail du prototype validé (`2026-07-20-terrain.html`). Le markup est réécrit,
 * seuls les tokens sont repris (D20). Les cibles tiennent leur plancher **par la cascade** : l'app
 * monte en `data-density="touch"` et le rail consomme `--row-h`, jamais une variante
 * tactile. Les cinq entrées du prototype y sont, « Historique » et « Réglages » comprises.
 *
 * La pastille de « Notifs » compte ce qui attend l'agent, pas des non-lus (cf.
 * {@link NotificationsRailItem}).
 *
 * `height="viewport"` n'est pas cosmétique : sans borne, le document entier défile, le pied
 * de rail fuit sous le pli et la barre d'onglets de l'éditeur part avec le contenu — c'est
 * ce que le prototype obtient par `#app { height: 100vh; overflow: hidden }`.
 */
function Shell() {
  const { auth } = shellRoute.useRouteContext()
  const session = useSyncExternalStore(auth.subscribe, auth.getSnapshot, auth.getSnapshot)

  return (
    <AppShell height="viewport">
      <nav className={s.rail} aria-label="Navigation principale">
        <span className={s.brand} aria-hidden>
          GHP
        </span>
        <RailItem to="/" icon={ClipboardList} label="Contrôles" exact />
        <RailItem to="/agenda" icon={CalendarDays} label="Agenda" />
        <NotificationsRailItem />
        <RailItem to="/history" icon={History} label="Historique" />
        <RailItem to="/settings" icon={Settings} label="Réglages" />
        <span className={s.spacer} />
        <ReportButton
          trigger={({ onClick, disabled }) => (
            <NavItem
              orientation="vertical"
              icon={<MessageSquareWarning size={23} aria-hidden />}
              label="Signaler"
              render={
                <button
                  type="button"
                  aria-label="Signaler un problème"
                  disabled={disabled}
                  onClick={onClick}
                />
              }
            />
          )}
        />
        <ConnectionState />
        {session.state !== 'signed_out' && <SignedInAgent user={session.user} />}
        <TeamBlock />
      </nav>

      <AppShellContent>
        <UpdatePrompt />
        <Outlet />
      </AppShellContent>
    </AppShell>
  )
}
